import Link from 'next/link';
import styled from 'styled-components';
import { rgba } from 'polished';
import { ExclamationCircle } from '@/components/uielements/icon/exclamation-circle';
import { StyledPrimaryButton } from '@/components/uielements/primaryButton/styles';
import { COLORS, LAYOUT } from '@/constants/styles';
import { ROUTES } from '@/constants/routes';

/**
 * Shown when coo-api returns 404 for a store location — the diner reached a real URL but the
 * backend cannot find the store right now. Instead of a silent redirect to the homepage (the
 * previous behavior), this gives the diner a clear message and a way forward. (TEAM-14936)
 */
export function LocationNotFound() {
  return (
    <StyledContainer role="alert" data-testid="location-not-found">
      <StyledIconContainer>
        <StyledExclamationCircle title="" />
      </StyledIconContainer>
      <StyledTextContainer>
        <StyledTitle>This restaurant isn&apos;t taking online orders right now</StyledTitle>
        <StyledMessage>
          The page you&apos;re looking for isn&apos;t available at the moment. Please try again
          later or browse other options.
        </StyledMessage>
      </StyledTextContainer>
      <Link href={ROUTES.HOMEPAGE} passHref legacyBehavior>
        <StyledHomeLink data-css-override="StyledPrimaryButton">
          Browse available restaurants
        </StyledHomeLink>
      </Link>
    </StyledContainer>
  );
}

const StyledContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  gap: '24px',
  padding: `${LAYOUT.GUTTER * 2}px ${LAYOUT.GUTTER}px`,
  maxWidth: '480px',
  margin: '0 auto',
});

const StyledIconContainer = styled.div({
  width: '80px',
  height: '80px',
  borderRadius: '100%',
  backgroundColor: rgba(COLORS.PRIMARY_WARNING, 0.13),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: `1px solid ${COLORS.PRIMARY_WARNING}`,
});

const StyledExclamationCircle = styled(ExclamationCircle)({
  width: '32px',
  height: '32px',
  color: COLORS.PRIMARY_WARNING,
});

const StyledTextContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '8px',
});

const StyledTitle = styled.span({
  fontSize: '24px',
  fontWeight: 700,
  lineHeight: '32px',
});

const StyledMessage = styled.span({
  fontSize: '16px',
  lineHeight: '24px',
});

const StyledHomeLink = styled(StyledPrimaryButton).attrs({ as: 'a' })({
  width: '100%',
  maxWidth: '320px',
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
});
